import React from "react";
import { Link } from "react-router-dom";
import PolicyPage from "./PolicyPage";
import "../css/delivery.scss";

const sections = [
  {
    id: "order-processing",
    title: "Order processing",
    content: (
      <>
        <p>
          Orders are normally prepared within 1–3 business days after payment
          has been confirmed. Orders placed on weekends or public holidays begin
          processing on the next business day.
        </p>
        <p>
          High-demand launches, large orders, payment reviews or address checks
          may require additional time. If a significant delay affects your
          order, we will contact you using the details supplied at checkout.
        </p>
        <p className="policy-note">
          Processing time is the time needed to prepare your order. It is
          separate from the carrier’s delivery time.
        </p>
      </>
    ),
  },
  {
    id: "shipping-options",
    title: "Shipping options and charges",
    content: (
      <p>
        Available delivery services, prices and estimated arrival windows are
        calculated from the items and destination entered at checkout. Some
        services may not be offered for remote locations or products subject to
        carrier restrictions. Any promotion for free delivery will state its
        qualifying order value and eligible destination.
      </p>
    ),
  },
  {
    id: "delivery-estimates",
    title: "Estimated delivery times",
    content: (
      <>
        <p>Typical estimates after dispatch are:</p>
        <ul>
          <li>
            <strong>Standard delivery:</strong> approximately 7–15 business days
          </li>
          <li>
            <strong>Remote destinations:</strong> approximately 10–20 business
            days
          </li>
          <li>
            <strong>Express delivery:</strong> shown at checkout where available
          </li>
        </ul>
        <p>
          These windows are estimates and can be affected by customs, weather,
          local holidays, carrier capacity or events outside our reasonable
          control. A delivery estimate is not a guaranteed arrival date.
        </p>
      </>
    ),
  },
  {
    id: "tracking",
    title: "Tracking your order",
    content: (
      <>
        <p>
          When tracking is available, we will email a dispatch confirmation with
          the carrier and tracking number. It can take 24–72 hours after
          dispatch for a carrier’s tracking page to show its first update.
        </p>
        <p>
          An order containing several items may arrive in separate packages. In
          that case, you may receive more than one tracking number and packages
          may arrive on different days without an additional delivery charge.
        </p>
      </>
    ),
  },
  {
    id: "address-changes",
    title: "Addresses and delivery attempts",
    content: (
      <>
        <p>
          Please check the recipient name, address, postal code and telephone
          number before submitting an order. Contact us as soon as possible if
          something is incorrect. We can request a change before dispatch, but
          cannot guarantee that a packed or shipped order can be redirected.
        </p>
        <p>
          If a package is returned because the address was incomplete, delivery
          was refused or collection was missed, we will contact you about
          redelivery or a refund. Additional carrier costs may be deducted where
          permitted by law.
        </p>
      </>
    ),
  },
  {
    id: "customs",
    title: "Customs, taxes and import charges",
    content: (
      <p>
        International shipments may be inspected and charged import duty, tax or
        a handling fee by the destination authority or carrier. Unless checkout
        clearly states that these charges are included, the recipient is
        responsible for them. Customs processing may extend the delivery time,
        and we cannot mark a commercial order as a gift or declare an inaccurate
        value.
      </p>
    ),
  },
  {
    id: "delays-loss",
    title: "Delays, loss and delivery issues",
    content: (
      <>
        <p>
          If tracking has not updated for seven business days, or the estimated
          delivery window has passed, contact us with your order number. We will
          review the tracking and, when appropriate, open an investigation with
          the carrier.
        </p>
        <p>
          Please also check with household members, neighbors, building staff
          and the carrier before reporting a package as missing. Carrier
          investigations can take several business days to complete.
        </p>
      </>
    ),
  },
  {
    id: "damage-contact",
    title: "Damaged packages and support",
    content: (
      <>
        <p>
          Inspect your order shortly after delivery. If an item is damaged,
          incorrect or incomplete, contact us promptly with the order number and
          clear photos of the item, packaging and shipping label. Keep all
          packaging until we confirm the next step.
        </p>
        <p>
          Email <a href="mailto:support@dzcp896.com">support@dzcp896.com</a>,
          call +852 5436 7186, or use our{" "}
          <Link to="/contact">contact page</Link>. For return eligibility and
          refund details, see our
          <Link to="/returns"> Returns & Refunds Policy</Link>.
        </p>
      </>
    ),
  },
];

function Delivery() {
  return (
    <PolicyPage
      className="delivery"
      title="Delivery Policy"
      summary="Find out how orders are processed, when to expect tracking and what to do if a shipment needs attention."
      sections={sections}
    />
  );
}

export default Delivery;
